import {html} from '@isceco/widget-library2/external/lit'
import WebComponent from '../../WebComponent'
import Navigation from '../Navigation/Navigation'
import PeriodenCalcDto from './PeriodenCalcDto';
import VerordnungDto from '../Verordnung/VerordnungDto'
import VerordnungService from '../Verordnung/VerordnungService'
import VerordnungPeriodeColumns from './VerordnungPeriodeColumns'
import VerordnungVerbrauchergruppeColumns from './VerordnungVerbrauchergruppeColumns'

import '@isceco/widget-library2/basic-elements/Form/Form.js'
import '@isceco/widget-library2/basic-elements/DateInput/DateInput.js'
import '@isceco/widget-library2/basic-elements/NumberInput/NumberInput.js'
import '@isceco/widget-library2/basic-elements/TextInput/TextInput.js'
import '@isceco/widget-library2/basic-elements/Title/Title'
import '@isceco/widget-library2/basic-elements/Button/Button'
import '@isceco/widget-library2/basic-elements/RadioGroup/RadioGroup.js'
import '@isceco/widget-library2/basic-elements/ModalDialog/ModalDialog.js'
import '@isceco/widget-library2/basic-elements/List/InputList.js'
import '@isceco/widget-library2/basic-elements/DropdownMultiselect/DropdownMultiselect'
import '../../components/Message/Message'
import BaseService from '../../BaseService'

export default class VerordnungEdit extends WebComponent {

  constructor() {
    super();
    this.verordnungService = new VerordnungService()
    this.showAbortDialog = false
    this.datumStringMorgen = new Date(Date.now() + (24 * 60 * 60 * 1000)).toISOString().split('T')[0]
  }

  get translationFile() {
    return './views/VerordnungEdit/i18n.json'
  }

  get isEdit() {
    return !isEmpty(getNavigationId())
  }

  connectedCallback() {
    super.connectedCallback()
    this.reload()
  }

  reload() {
    Promise.all([
      this._fetchVerordnung(),
      this.verordnungService.listArt(),
      this.verordnungService.listDauerPeriode(),
      new BaseService('referenzjahren').list(),
      this.verordnungService.listReferenzart(),
      this.verordnungService.listVerbrauchergruppe()
    ]).then(([verordnung, artList, dauerPeriodeList, referenzjahrList, referenzartList, verbGruppeList]) => {
      this.artList = this.mapToDropdownOptions(artList, 'art')
      this.dauerPeriodeList = this.mapToDropdownOptions(dauerPeriodeList, 'dauerPeriode')
      this.referenzjahrList = referenzjahrList.map(it => ({value: it, name: it}))
      this.referenzartList = this.mapToDropdownOptions(referenzartList, 'referenzart')
      this.verbGruppeList = this.mapToDropdownOptions(verbGruppeList, 'verbrauchergruppe')
      this.verordnung = verordnung
      this.render()
    }).catch(_ => {
      this.verordnung = new VerordnungDto()
      this.render()
    })
  }

  _fetchVerordnung() {
    if (this.isEdit) {
      return this.verordnungService.read(getNavigationId())
        .then(json => new VerordnungDto(json))
    }
    // Neue Verordnung
    const verordnung = new VerordnungDto()
    verordnung.verbrauchergruppen = [{gruppe: 'ALLE'}]
    return Promise.resolve(verordnung)
  }

  mapToDropdownOptions(list, translationKey) {
    return list.map(item => ({
      value: item,
      name: this.i18n.translate(`${translationKey}.${item.toLowerCase()}`)
    }))
  }

  handleFieldChange(e) {
    const fieldName = e.target.getAttribute('name');
    this.verordnung[fieldName] = e.detail.value;
    this.render();
  }

  getTemplate() {
    const title = `verordnung.${this.isEdit ? 'edit' : 'create'}.title`
    return html`
      <isceco-title id="page-title" text="${this.i18n.translate(title)}"></isceco-title>
      <isceco-form id="verordnung-create-form"
                   prevent-navigation="true"
                   submit-button-text="${this.i18n.translate('base.button.submit')}"
                   @submit="${e => this._handleFormSubmit(e.detail)}"
                   abort-button-text="${this.i18n.translate('base.button.cancel')}"
                   @abort="${_ => this._handleFormAbort()}"
                   prevent-navigation
      >
        <div slot="form-elements" class="form-container">
          <div class="form-row">
            <isceco-radio-group name="art"
                                label="${this.i18n.translate('verordnung.create.art')} *"
                                .items="${this.artList}"
                                value="${this.verordnung.art}"
                                required
                                @change="${e => this.handleFieldChange(e)}"
            ></isceco-radio-group>
          </div>

          <div class="form-row">
            <isceco-text-input name="srNummer"
                               label="${this.i18n.translate('verordnung.create.srNummer')} *"
                               value="${this.verordnung.srNummer}"
                               required
            ></isceco-text-input>
            <isceco-text-input name="link"
                               label="${this.i18n.translate('verordnung.create.link')}"
                               value="${this.verordnung.link}"
                               maxlength="255"
            ></isceco-text-input>
          </div>

          <div class="form-row">
            <isceco-date-input name="geltungsdauerVon"
                               label="${this.i18n.translate('verordnung.create.geltungsdauerVon')} *"
                               value="${this.verordnung.geltungsdauerVon}"
                               min="${this.datumStringMorgen}"
                               open-on-click
                               required
                               @change="${e => this.handleFieldChange(e)}"
            ></isceco-date-input>
            <isceco-date-input name="geltungsdauerBis"
                               label="${this.i18n.translate('verordnung.create.geltungsdauerBis')} *"
                               value="${this.verordnung.geltungsdauerBis}"
                               min="${this.getGeltungsdauerMinDate()}"
                               open-on-click
                               required
                               @change="${e => this.handleFieldChange(e)}"
            ></isceco-date-input>
          </div>

          <div class="form-row">
            <isceco-radio-group name="dauerPeriode"
                                label="${this.i18n.translate('verordnung.create.dauerPeriode')} *"
                                .items="${this.dauerPeriodeList}"
                                value="${this.verordnung.dauerPeriode}"
                                required
                                @change="${e => {
                                  this.handleFieldChange(e)
                                  this.validateStartPerioden()
                                }}"
            ></isceco-radio-group>
          </div>
          ${this.renderStartPerioden()}
          ${this.renderInputDauer()}
          ${this.renderPeriodenList()}
          ${this.renderBerechnungsartInput()}
          ${this.renderBerechnungsartTyptagInput()}
          <div class="form-row">
            <isceco-dropdown-multiselect name="referenzjahr"
                                         label="${this.i18n.translate('verordnung.create.referenzjahr')} *"
                                         .items="${this.referenzjahrList}"
                                         .value="${this.verordnung.referenzjahr}"
                                         @change="${e => this.handleFieldChange(e)}"
                                         required
            ></isceco-dropdown-multiselect>
          </div>
          ${this.renderReferenzartInput()}
          <div class="form-row">
            <isceco-radio-group name="schwellwertAktiv"
                                label="${this.i18n.translate('verordnung.create.schwellwertAktiv')} *"
                                .items="${[
                                  {value: true, name: this.i18n.translate('base.button.yes')},
                                  {value: false, name: this.i18n.translate('base.button.no')}
                                ]}"
                                value="${this.verordnung.schwellwertAktiv}"
                                required
                                @change="${e => this.handleFieldChange(e)}"
            ></isceco-radio-group>
          </div>
          ${this.renderSchwellenwertInput()}
          <div class="form-row">
            ${this.renderVerbrauchergruppenList()}
          </div>
          ${this.renderBriefversandPflichtInput()}
        </div>
      </isceco-form>
      ${this._renderAbortDialog()}
    `
  }

  renderStartPerioden() {
    if (!this.verordnung.dauerPeriode) {
      return html``
    }
    return html`
      <div class="form-row">
        <isceco-date-input name="startPerioden"
                           class="form-element-half-width"
                           label="${this.i18n.translate('verordnung.create.startPerioden')} *"
                           value="${this.verordnung.startPerioden}"
                           min="${this.getGeltungsdauerMinDate()}"
                           open-on-click
                           required
                           @change="${e => {
                             this.handleFieldChange(e)
                             this.validateStartPerioden()
                           }}"
        ></isceco-date-input>
      </div>
    `
  }

  renderInputDauer() {
    if (!(this.verordnung.geltungsdauerBis && this.verordnung.dauerPeriode && this.verordnung.startPerioden)) {
      return html``
    }
    return html`
      <div class="form-row">
        <isceco-number-input name="anzahlZeit"
                             class="form-element-half-width"
                             label="${this.i18n.translate('verordnung.create.anzahl.' + this.verordnung.dauerPeriode.toLowerCase())} *"
                             value="${this.verordnung.anzahlZeit}"
                             maximum-fraction-digits="0"
                             required
                             @keyup="${e => this.handleFieldChange(e)}"
                             @change="${e => this.handleFieldChange(e)}"
        ></isceco-number-input>
        <div style="padding-top: 24px">
          <isceco-button name="calculatePerioden"
                         text="${this.i18n.translate('verordnung.create.perioden.button')}"
                         icon="sync"
                         variant="primary"
                         ?disabled="${this.isCalcPeriodenButtonDisabled()}"
                         @click="${_ => this.calculatePerioden()}">
          </isceco-button>
        </div>
      </div>
    `
  }

  renderPeriodenList() {
    if (this.verordnung.perioden.length === 0) {
      return html``
    } else if (this.verordnung.perioden.length === 1 && this.periodeTruncated) {
      return html`
        <div class="form-row">
          <eko-message type="error" .closable="${false}"
                       message="${this.i18n.translate('verordnung.create.periode.tooLong')}"
          ></eko-message>
        </div>
      `
    }
    const warning = this.periodeTruncated ? html`
      <div class="form-row">
        <eko-message type="warning"
                     message="${this.i18n.translate('verordnung.create.periode.truncated')}"
        ></eko-message>
      </div>
    ` : html``
    return html`
      ${warning}
      <div class="form-row">
        <isceco-input-list id="verordnung-perioden-list"
                           name="perioden"
                           .i18n="${this.i18n}"
                           .columns="${VerordnungPeriodeColumns.getColumns()}"
                           .data="${this.verordnung.perioden}"
        ></isceco-input-list>
      </div>
    `
  }

  renderBerechnungsartInput() {
    if (!this.verordnung.dauerPeriode || !this.verordnung.anzahlZeit) {
      return html``
    }
    const berechnungsartList = []
    if (this.verordnung.dauerPeriode === 'MONAT') {
      berechnungsartList.push({value: 'KONTINGENT', name: this.i18n.translate('berechnungsart.kontingent')})
    } else if (this.verordnung.dauerPeriode === 'TAG' && this.verordnung.anzahlZeit === '1') {
      berechnungsartList.push({value: 'SOFORT', name: this.i18n.translate('berechnungsart.sofort')})
    }
    berechnungsartList.push({value: 'TYPTAG', name: this.i18n.translate('berechnungsart.typtag')})
    this.verifyBerechnungsart(berechnungsartList)
    return html`
        <div class="form-row">
          <isceco-radio-group name="berechnungsart"
                              label="${this.i18n.translate('verordnung.create.berechnungsart')} *"
                              .items="${berechnungsartList}"
                              value="${this.verordnung.berechnungsartMain}"
                              required
                              @change="${e => this.handleFieldChange(e)}"
          ></isceco-radio-group>
        </div>
      `
  }

  verifyBerechnungsart(berechnungsartList) {
    // Beim Anpassen von `dauerPeriode` sicherstellen, dass die ausgewählte `berechnungsart` noch eine valide Option ist
    if (this.verordnung.berechnungsart) {
      const valid = berechnungsartList.find(item => item.value === this.verordnung.berechnungsartMain)
      if (!valid) {
        this.verordnung.berechnungsart = null
      }
    }
  }

  renderBerechnungsartTyptagInput() {
    if (!this.verordnung.berechnungsart?.startsWith('TYPTAG')) {
      return html``
    }
    const berechnungsartTyptagList = [
      {value: 'MEDIAN', name: this.i18n.translate('berechnungsart.typtag_median')},
      {value: 'DURCHSCHNITT', name: this.i18n.translate('berechnungsart.typtag_durchschnitt')},
    ]
    const updateBerechnungsartTyptag = e => {
      this.verordnung.berechnungsart = 'TYPTAG_' + e.detail.value
      this.render()
    }
    return html`
        <div class="form-row">
          <isceco-radio-group name="berechnungsartSub"
                              label="${this.i18n.translate('verordnung.create.berechnungsart.typtag')} *"
                              .items="${berechnungsartTyptagList}"
                              value="${this.verordnung.berechnungsartSub}"
                              required
                              @change="${updateBerechnungsartTyptag}"
          ></isceco-radio-group>
        </div>
      `
  }

  renderReferenzartInput() {
    if (this.verordnung.referenzjahr.length < 3) {
      return html``
    }
    return html`
      <isceco-radio-group name="referenzart"
                          label="${this.i18n.translate('verordnung.create.referenzart')} *"
                          .items="${this.referenzartList}"
                          value="${this.verordnung.referenzart}"
                          required
      ></isceco-radio-group>
    `
  }

  renderSchwellenwertInput() {
    if (!this.verordnung.schwellwertAktiv) {
      return html``
    }
    return html`
      <div class="form-row">
        <isceco-number-input name="schwellwert"
                             class="form-element-half-width"
                             label="${this.i18n.translate('verordnung.create.schwellwert')}"
                             value="${this.verordnung.schwellwert}"
                             maximum-fraction-digits="2"
                             required
        ></isceco-number-input>
      </div>
    `
  }

  renderVerbrauchergruppenList() {
    return html`
      <isceco-input-list id="verordnung-verbrauchergruppen-list"
                         name="verbrauchergruppen"
                         .i18n="${this.i18n}"
                         .columns="${VerordnungVerbrauchergruppeColumns.getColumns(this.verbGruppeList)}"
                         .data="${this.verordnung.verbrauchergruppen}"
      ></isceco-input-list>`
  }

  renderBriefversandPflichtInput() {
    if (this.verordnung.art !== 'KONT') {
      return html``
    }
    return html`
      <div class="form-row">
        <isceco-radio-group name="briefversandPflicht"
                            label="${this.i18n.translate('verordnung.create.briefversandPflicht')} *"
                            .items="${[
                              {value: false, name: this.i18n.translate('verordnung.create.briefversandPflicht.no')},
                              {value: true, name: this.i18n.translate('verordnung.create.briefversandPflicht.yes')}
                            ]}"
                            value="${this.verordnung.briefversandPflicht}"
                            required
        ></isceco-radio-group>
      </div>
    `
  }

  _renderAbortDialog() {
    if(this.showAbortDialog) {
      return html`
        <isceco-dialog id="abort-dialog"
                       header="${this.i18n.translate('base.abort.dialog.header')}"
                       description="${this.i18n.translate('base.abort.dialog.description')}"
                       confirm-button="${this.i18n.translate('base.button.yes')}"
                       cancel-button="${this.i18n.translate('base.button.no')}"
                       @submit="${event => this._handleAbortConfirmed(event.detail)}"
        ></isceco-dialog>
      `
    } else {
      return html``
    }
  }

  validateStartPerioden = _ => {
    if (this.verordnung.startPerioden) {
      const date = new Date(this.verordnung.startPerioden)
      const input = document.querySelector(`[name="startPerioden"]`)
      if (this.verordnung.dauerPeriode === 'WOCHE' && date.getDay() !== 1) {
        input.setError(this.i18n.translate('verordnung.create.startPerioden.error.woche'))
      } else if (this.verordnung.dauerPeriode === 'MONAT' && date.getDate() !== 1) {
        input.setError(this.i18n.translate('verordnung.create.startPerioden.error.monat'))
      } else {
        input.setError()
      }
      this.render()
    }
  }

  calculatePerioden() {
    const calcObj = {
      von: this.verordnung.startPerioden,
      bis: this.verordnung.geltungsdauerBis,
      dauer: this.verordnung.dauerPeriode,
      anzahl: this.verordnung.anzahlZeit
    }
    this.verordnungService.calculatePerioden(calcObj)
      .then(json => {
        const dto = new PeriodenCalcDto(json)
        this.verordnung.perioden = dto.perioden;
        this.periodeTruncated = dto.truncated;
        this.render();
      })
  }

  _handleFormAbort = () => {
    this.showAbortDialog = true
    this.render()
  }

  _handleAbortConfirmed = event => {
    if (event.confirmed) {
      if (this.isEdit) {
        navigate({to: Navigation.ROUTES.verordnungDetail.url, id: getNavigationId(), allow: true})
      } else {
        navigate({to: Navigation.ROUTES.verordnung.url, allow: true})
      }
    } else {
      this.showAbortDialog = false
      this.render()
    }
  }

  _handleFormSubmit = verordnung => {
    if (this.isEdit) {
      verordnung.id = getNavigationId();
      verordnung.berechnungsart = this.verordnung.berechnungsart;
      this.verordnungService.update(verordnung)
        .then(updated => {
          window.showAlert(this.i18n.translate('verordnung.edit.alert.success', {srNummer: updated.srNummer}), "", 'success')
          navigate({to: Navigation.ROUTES.verordnung.url, id: `${updated.id}`, allow: true})
        })
    } else {
      verordnung.berechnungsart = this.verordnung.berechnungsart;
      this.verordnungService.create(verordnung)
        .then(created => {
          window.showAlert(this.i18n.translate('verordnung.create.alert.success', {srNummer: created.srNummer}), "", 'success')
          navigate({to: Navigation.ROUTES.verordnung.url, id: `${created.id}`, allow: true})
        })
    }
  }

  getGeltungsdauerMinDate = () => this.verordnung.geltungsdauerVon ? this.verordnung.geltungsdauerVon : this.datumStringMorgen
  isCalcPeriodenButtonDisabled = () => !(this.verordnung.geltungsdauerBis && this.verordnung.dauerPeriode && this.verordnung.anzahlZeit && this.verordnung.startPerioden)

}
customElements.define('eko-frontend-verordnung-edit', VerordnungEdit)
